/**
 * OfflineSyncManager — side-effect component (renders null).
 * Flushes the offline session write queue when connection restores.
 * pwa-offlineSKILL-final.md — File 7.
 * Rule 3 — flushes automatically on reconnect, no data loss.
 */
import { useCallback, useEffect } from 'react'
import { useOnlineStatus }   from '@/hooks/useOnlineStatus'
import { flushOfflineQueue } from '@/lib/offline-queue'
import { useToast }          from '@/context/ToastContext'

const RECONNECT_DELAY_MS = 2_000

export function OfflineSyncManager() {
  const { reconnectedAt } = useOnlineStatus()
  const { toast }         = useToast()

  const flush = useCallback(() => {
    flushOfflineQueue().then(({ flushed, failed }) => {
      if (flushed > 0) toast(`Synced ${flushed} session${flushed > 1 ? 's' : ''}`, 'success')
      if (failed  > 0) toast(`${failed} session${failed > 1 ? 's' : ''} could not sync`, 'error')
    })
  }, [toast])

  // Flush any items queued during a previous offline session
  useEffect(() => {
    if (navigator.onLine) flush()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Flush on reconnect — delay 2s to let the network stabilise before writing
  useEffect(() => {
    if (!reconnectedAt) return
    const timer = setTimeout(flush, RECONNECT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [reconnectedAt, flush])

  return null
}
